import React, { useState } from "react";
import { useSelector } from "react-redux";

import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { selectProfile } from "@/redux/selectors/authSelectors";

import { useGoalAndObjectiveText } from "../../api/queries/useGoalAndObjectiveText";
import { useGoalsAndObjectives } from "../../api/queries/useGoalsAndObjectives";

import { CareGoalModal } from "./components/CareGoalModal";
import { CareGoalRoleEntry } from "./components/CareGoalRoleEntry";
import {
  flattenGoals,
  flattenObjectives,
  getCurrentCareTeamMembersByType,
  getGoalsMap,
  getObjectivesMap,
  getUniqueCurrentAndPastCareTeamRoles,
} from "./helpers";
import { MODAL_MODES, PROVIDER_TYPE_TO_LABEL } from "./constants";

import "./GoalsAndObjectives.styles.scss";

export function GoalsAndObjectives(props) {
  const profile = useSelector(selectProfile);

  const { data: goalAndObjectives = {}, refetch: refetchGoalsAndObjectives } =
    useGoalsAndObjectives({ patientId: props.patientId });
  const { data: goalsAndObjectiveOptions } = useGoalAndObjectiveText({
    patientId: props.patientId,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(MODAL_MODES.ADD_GOAL);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [objectiveCloseMode, setObjectiveCloseMode] = useState(null);

  const providerType = profile?.provider_type;
  const [activeEditingProviderType, setActiveEditingProviderType] = useState(providerType);

  const rolesToShow = getUniqueCurrentAndPastCareTeamRoles(props.careTeamMembers);
  const careTeamMembersByRole = getCurrentCareTeamMembersByType(props.careTeamMembers);

  const providerGoalsAndObjectiveOptions =
    goalsAndObjectiveOptions?.[activeEditingProviderType] || [];
  const providerGoalOptions = flattenGoals(providerGoalsAndObjectiveOptions);
  const providerObjectiveOptions = flattenObjectives(providerGoalsAndObjectiveOptions);
  const providerGoalOptionsMap = getGoalsMap(providerGoalsAndObjectiveOptions);
  const providerObjectiveOptionsMap = getObjectivesMap(providerGoalsAndObjectiveOptions);
  const providerLabel = PROVIDER_TYPE_TO_LABEL?.[activeEditingProviderType] || "";

  const onCloseModal = () => {
    setShowModal(false);
    setModalMode(MODAL_MODES.EMPTY);
    setSelectedGoal(null);
    setSelectedObjective(null);
    setObjectiveCloseMode(null);
  };

  const onModalSubmission = async () => {
    await refetchGoalsAndObjectives();
    onCloseModal();
  };

  const getProviderTypeFromGoal = (goal) => goal.provider_type;

  const openAddGoalModal = (role) => {
    setActiveEditingProviderType(role);
    setModalMode(MODAL_MODES.ADD_GOAL);
    setShowModal(true);
  };

  const openAddObjectiveModal = (goal) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.ADD_OBJECTIVE);
    setSelectedGoal(goal);
    setShowModal(true);
  };

  const openEditObjectiveModal = (goal, objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.EDIT_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openCompleteObjectiveModal = (goal, objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.COMPLETE_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openRemoveObjectiveModal = (goal, objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.REMOVE_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openCloseAndAddObjectiveModal = (goal, objective, newObjectiveStatus) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.CLOSE_AND_ADD_OBJECTIVE);
    setSelectedObjective(objective);
    setSelectedGoal(goal);
    setObjectiveCloseMode(newObjectiveStatus);
    setShowModal(true);
  };

  if (rolesToShow.length === 0) {
    return null;
  }

  return (
    <DashboardCard2 title="Care Goals & Objectives">
      <div className="CareGoalsCard--role_container">
        <CareGoalModal
          goal={selectedGoal}
          modalMode={modalMode}
          objective={selectedObjective}
          objectiveCloseMode={objectiveCloseMode}
          onClose={onCloseModal}
          onCloseAndAddObjective={openCloseAndAddObjectiveModal}
          onCompleteObjectiveClick={openCompleteObjectiveModal}
          onModalSubmission={onModalSubmission}
          onRemoveObjectiveClick={openRemoveObjectiveModal}
          patientDetails={props.patientDetails}
          providerGoalOptions={providerGoalOptions}
          providerGoalOptionsMap={providerGoalOptionsMap}
          providerLabel={providerLabel}
          providerObjectiveOptions={providerObjectiveOptions}
          providerObjectiveOptionsMap={providerObjectiveOptionsMap}
          providerTypeCurrentGoalsAndObjectives={goalAndObjectives[activeEditingProviderType]}
          show={showModal}
        />

        {Object.keys(PROVIDER_TYPE_TO_LABEL).map((key) => {
          if (!rolesToShow.includes(PROVIDER_TYPE_TO_LABEL[key])) {
            return null;
          }

          return (
            <CareGoalRoleEntry
              careTeamMembersForRole={careTeamMembersByRole?.[PROVIDER_TYPE_TO_LABEL[key]]}
              isProviderOnCareTeam={props.isProviderOnCareTeam}
              key={key}
              label={PROVIDER_TYPE_TO_LABEL[key]}
              onAddGoalClick={() => openAddGoalModal(key)}
              onAddObjectiveClick={openAddObjectiveModal}
              onCompleteObjectiveClick={openCompleteObjectiveModal}
              onEditObjectiveClick={openEditObjectiveModal}
              onRemoveObjectiveClick={openRemoveObjectiveModal}
              patientId={props.patientId}
              profile={profile}
              providerType={providerType}
              providerTypeCurrentGoalsAndObjectives={goalAndObjectives[key]}
              refreshCareTeam={props.refreshCareTeam}
              role={key}
            />
          );
        })}
      </div>
    </DashboardCard2>
  );
}
